// Preset shape and storage. A preset is a snapshot of every UI module's
// state plus the few synth-level values (bpm) that don't live in a
// module. Stored in localStorage as a single JSON blob under
// `LOCAL_KEY`; factory presets are inlined and read-only.

import type { OscState } from "../ui/osc-module.ts";
import type { FilterState } from "../ui/filter-module.ts";
import type { EnvState } from "../ui/env-module.ts";
import type { ModEnvState } from "../ui/mod-env-module.ts";
import type { LfoState } from "../ui/lfo-module.ts";
import type { EqState } from "../ui/eq-module.ts";
import type { DistortionState } from "../ui/distortion-module.ts";
import type { DelayState } from "../ui/delay-module.ts";
import type { ReverbState } from "../ui/reverb-module.ts";
import type { MasterState } from "../ui/master-module.ts";

/** Schema version. Bump when the preset shape changes incompatibly so
 *  older stored presets can be migrated (or dropped). */
export const PRESET_VERSION = 1;

export interface Preset {
  version: number;
  name: string;
  osc1: OscState;
  osc2: OscState;
  filter: FilterState;
  ampEnv: EnvState;
  modEnv: ModEnvState;
  lfo: LfoState;
  bpm: number;
  eq: EqState;
  distortion: DistortionState;
  delay: DelayState;
  reverb: ReverbState;
  master: MasterState;
  fxOrder: string[];
}

/** Source identifier for a stored preset. Factories are read-only and
 *  shipped with the build; user presets live in localStorage. */
export type PresetSource = "factory" | "user";

export interface StoredPreset {
  source: PresetSource;
  preset: Preset;
}

const LOCAL_KEY = "antinode-presets";

/** Read user presets from localStorage. Returns `[]` on parse error so a
 *  corrupted blob can't brick the synth — the UI will just show
 *  factories. */
export function loadUserPresets(): Preset[] {
  try {
    const raw = localStorage.getItem(LOCAL_KEY);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((p): p is Preset => isPreset(p));
  } catch {
    return [];
  }
}

export function saveUserPresets(presets: Preset[]): void {
  try {
    localStorage.setItem(LOCAL_KEY, JSON.stringify(presets));
  } catch (err) {
    // Quota exceeded, private browsing, etc. — log but don't crash.
    console.warn("[preset] localStorage save failed", err);
  }
}

function isPreset(p: unknown): p is Preset {
  if (typeof p !== "object" || p === null) return false;
  const obj = p as Record<string, unknown>;
  return (
    typeof obj.name === "string" &&
    typeof obj.version === "number" &&
    obj.version <= PRESET_VERSION &&
    typeof obj.osc1 === "object" &&
    typeof obj.filter === "object" &&
    typeof obj.ampEnv === "object"
  );
}
